import { useEffect, useState, useCallback } from 'react';
import { useContext } from 'react';
import {useNavigate} from "react-router-dom";

import {faArrowUpRightFromSquare, faFlag} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Row, Col}  from 'react-bootstrap';

import {DataGrid, GridActionsCellItem} from "@mui/x-data-grid";
import Checkbox from '@mui/material/Checkbox';
import NavigationHeader from "../Layout/NavigationHeader";

import {getPatientSessionList} from '../../common/BackendConnections';
import PatientContext from "../../store/patient-context";

import './PatientSessions.css'


const getCuratedSessionList = (patientID) => {
  return getPatientSessionList(patientID)
    .then((response) => {
        return response;
      }
    )
    .catch((function(err) { console.log('fetch error from getPatientSessionList\n' + err); })
    );
}


const PatientSessions = (props) => {
  const navigate = useNavigate();
  const patientCtx = useContext(PatientContext);
  const [patientSessions, setPatientSessions] = useState([]);
  const [selectedSessionIds, setSelectedSessionIds] = useState([]);
  const [displayComparisonButton, setDisplayComparisonButton] = useState(false);

  const createDefaultPatientList = useCallback( () => {
    getCuratedSessionList(patientCtx.patientId).then((response) => {
      const sessionData = response.data["session_list"];
      const valueOrDash = (value) => {return value ? value : '-'}

      const loadedUsers = [];
      for (const key in sessionData) {
        const index = +key
        const localDate = new Date(sessionData[index].datetime_iso8601)
        loadedUsers.push({
          index: index,
          patient_id: sessionData[index].patient_id,
          scene_id: sessionData[index].scene_id,
          session_id: sessionData[index].session_id,
          observer_id: sessionData[index].observer_id,
          datetime: sessionData[index].datetime_iso8601,
          id: sessionData[index].session_short_id,  
          game: sessionData[index].scene_name,
          Clinician: sessionData[index].observer_name,
          Platform: valueOrDash(sessionData[index].device_name),
          Date: `${localDate.toLocaleDateString()} - ${localDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`,
          Flag: false
        });
      }
      setPatientSessions(loadedUsers);
    }).catch(function(err) {})
  }, [patientCtx.patientId])


  useEffect(() => {
    createDefaultPatientList();
  }, [createDefaultPatientList]);
  
  const handleSessionComparison = (event) => {
    event.preventDefault();
    if (selectedSessionIds.length === 2) {
      const session1 = selectedSessionIds[0];
      const session2 = selectedSessionIds[1];
      //console.log("session1 from PatientSession before passing to session_comparison = ", session1);
      navigate(`/session_comparison/${session1}/${session2}`);
    }
    else {
      console.log("2 sessions must be selected to compare")
    }
  };

  const handleSelectPatient = (event) => {
    event.preventDefault();
    navigate("/patient_selection/");
  };


  const onSessionSelection = (event, session_id) => {
    let new_array = [...selectedSessionIds];

    const index = new_array.indexOf(session_id);
    if (index > -1) {
      // The id is already in the array, we remove it
      new_array.splice(index, 1);
    } else {
      new_array.push(session_id);
      if (new_array.length > 2) {
        new_array.shift();  // pop left
      }
    }

    if (new_array.length === 2) {
      setDisplayComparisonButton(true);
    }
    if (new_array.length < 2) {
      setDisplayComparisonButton(false);
    }
    setSelectedSessionIds(new_array);
  };


  const handleSessionSummaryButtonClick = (event, id) => {
    event.preventDefault();
    event.stopPropagation();
    navigate("/session_info/" + id);
  };

  const columns = [
    { field: 'id', headerName: 'ID#', flex: 0},
    { field: 'session_id', headerName: 'Session ID', flex: 1},
    { field: 'game', headerName: 'Game', flex: 1},
    { field: 'Clinician', headerName: 'Facilitator', flex: 1},
    { field: 'Platform', headerName: 'Platform', flex: 1},
    { field: 'Date', headerName: 'Date', flex: 1},
    { field: 'compare', headerName: 'Compare', flex: 0, type: 'actions',
      getActions: (params) => [
        <Checkbox
          color="primary"
          checked={selectedSessionIds.includes(params.row.session_id)}
          onClick={(event) => onSessionSelection(event, params.row.session_id)}
          inputProps={{ 'aria-label': 'controlled'}}
        />
      ]
    },
    { field: 'summary', headerName: "Summary", type: 'actions', flex: 0,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} className={"fa-1x"} />}
          label="View summary"
          onClick={(event) => handleSessionSummaryButtonClick(event, params.row.session_id)}
        />
    ]},
    { field: 'Flag', headerName: "", type: 'actions', flex:0, width:20,

      getActions: (params) => [
        <GridActionsCellItem
          icon={(params.row.Flag)? <FontAwesomeIcon icon={faFlag} className={"fa-1x"} style={{color: "#E72121"}}/>: <span style={{display: "none"}}/>}
          label="Flag"
          /*onClick={(event) => handleSessionFlagButtonClick(event, params.row.id)}*/
        />
      ]}
  ];

  return (
    <div className={"page_content"}>
      <NavigationHeader />

      <div>
        <Row>
          <Col xs={5}>
            <div><h1>Patient Sessions</h1></div>
          </Col>
          <Col xs={{ span: 2, offset:3 }}>
            <div>
              <button id="selectButton" className={"sessionSelect-Button"}
                      onClick={handleSelectPatient}>
                Select/Add Patient
              </button>
            </div>
          </Col>
          <Col xs={{ span: 2, offset:0 }}>
            <div>
              <button id="compareButton" className={"sessionCompare-Button"} disabled={!displayComparisonButton}
                      onClick={handleSessionComparison}>
                Compare Sessions
              </button>
            </div>
          </Col>
        </Row>

          <div style={{ height: '60vh', width: '100%' }}>
            <DataGrid
              columnVisibilityModel={{
                // Hide columns session_id, the other columns will remain visible
                session_id: false,
              }}

              id="datagridPatientSessions"
              rows={patientSessions}
              columns={columns}
              pageSizeOptions={[5, 10, 25]}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 5, page: 0 },
                },
              }}
            />
          </div>
      </div>
    </div>
  );
}

export default PatientSessions;
